// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useEffect, useState } from 'react';

import { scrollableModalBodyStyle } from '../../../styles/constants';
import { positionFilterAPI, PositionFilterSetting } from '../../../utils/apiClient';
import { Button } from '../../common/Button';

interface Props {
  inline?: boolean;
  onClose: () => void;
}

export const PositionFilterSettings: React.FC<Props> = ({ inline, onClose }) => {
  const [settings, setSettings] = useState<PositionFilterSetting[]>([]);
  const [availableFilters, setAvailableFilters] = useState<string[]>([]);
  const [edited, setEdited] = useState<Record<string, string | number | boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await positionFilterAPI.getSettings();
      if (!data.success) throw new Error((data as any).error || 'Failed to load');
      setSettings(data.settings);
      setAvailableFilters(data.availableFilters);
      const init: Record<string, string | number | boolean> = {};
      data.settings.forEach((s) => {
        init[s.setting_key] = s.setting_value;
      });
      setEdited(init);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = () => settings.some((s) => edited[s.setting_key] !== s.setting_value);

  const handleSave = async () => {
    if (!hasChanges()) return;
    if (
      !window.confirm(
        'Apply position filter changes? They take effect immediately for new positions.'
      )
    )
      return;
    try {
      setSaving(true);
      const changed = settings.filter((s) => edited[s.setting_key] !== s.setting_value);
      for (const s of changed) {
        const result = await positionFilterAPI.updateSetting(s.setting_key, edited[s.setting_key]);
        if (!result.success) throw new Error(`Failed to update ${s.setting_key}`);
      }
      await load();
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const containerStyle: React.CSSProperties = inline
    ? {}
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 'max(20px, 5vh)',
        zIndex: 5000,
      };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '3vmin', fontSize: '2vmin', color: '#666' }}>
          Loading settings...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle} onClick={inline ? undefined : onClose}>
        <div
          style={{ background: 'white', padding: '3vmin', borderRadius: '1vmin' }}
          onClick={inline ? undefined : (e) => e.stopPropagation()}
        >
          <p style={{ color: '#f44' }}>{error}</p>
          <Button
            text="Close"
            onClick={onClose}
            style={{
              background: '#ccc',
              color: '#333',
              padding: '1vmin 2vmin',
              fontSize: '1.6vmin',
            }}
          />
        </div>
      </div>
    );
  }

  const filterSetting = settings.find((s) => s.setting_key === 'filter');
  const booleanSettings = settings.filter((s) => s.unit === 'boolean');
  const numericSettings = settings.filter(
    (s) => s.setting_key !== 'filter' && s.unit !== 'boolean'
  );
  const currentFilter = String(edited['filter'] ?? '');

  return (
    <div style={containerStyle} onClick={inline ? undefined : onClose}>
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          ...(inline ? {} : { maxWidth: '650px', width: '90%', maxHeight: 'min(90vh, 85dvh)' }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: inline ? 'none' : '0 8px 16px rgba(0,0,0,0.3)',
        }}
        onClick={inline ? undefined : (e) => e.stopPropagation()}
      >
        {/* Scrollable body */}
        <div
          style={{
            ...scrollableModalBodyStyle(inline),
            padding: '2.5vmin',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2vmin',
              borderBottom: '2px solid #ddd',
              paddingBottom: '1.5vmin',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '2.5vmin', color: '#333' }}>
              Position Filter Settings
            </h2>
            {!inline && (
              <Button
                text="✕"
                onClick={onClose}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                  borderRadius: '0.3vmin',
                }}
              />
            )}
          </div>

          <div
            style={{
              background: '#e3f2fd',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              marginBottom: '2vmin',
              fontSize: '1.5vmin',
              border: '1px solid #2196F3',
              color: '#1565C0',
            }}
          >
            Changes take effect immediately for new positions. Previously stored positions are not
            affected.
          </div>

          {/* Filter selector */}
          {filterSetting && (
            <div
              style={{
                marginBottom: '2.5vmin',
                background: '#f9f9f9',
                padding: '1.5vmin',
                borderRadius: '0.5vmin',
                border: '1px solid #ddd',
              }}
            >
              <h3 style={{ margin: '0 0 1vmin 0', fontSize: '2vmin', color: '#333' }}>
                Filter Algorithm
              </h3>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1vmin',
                  padding: '1vmin',
                  background: 'white',
                  borderRadius: '0.3vmin',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '1.6vmin', fontWeight: 'bold' }}>
                    {filterSetting.description}
                  </div>
                  <div style={{ fontSize: '1.3vmin', color: '#888' }}>filter</div>
                </div>
                <select
                  value={currentFilter}
                  onChange={(e) => setEdited((prev) => ({ ...prev, filter: e.target.value }))}
                  style={{
                    fontSize: '1.6vmin',
                    padding: '0.5vmin 1vmin',
                    border:
                      currentFilter !== String(filterSetting.setting_value)
                        ? '2px solid #2196F3'
                        : '1px solid #ccc',
                    borderRadius: '0.3vmin',
                    minWidth: '120px',
                  }}
                >
                  <option value="">Disabled</option>
                  {availableFilters.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Boolean settings */}
          {booleanSettings.length > 0 && (
            <div
              style={{
                background: '#f9f9f9',
                padding: '1.5vmin',
                borderRadius: '0.5vmin',
                border: '1px solid #ddd',
                marginBottom: '2.5vmin',
              }}
            >
              <h3 style={{ margin: '0 0 1vmin 0', fontSize: '2vmin', color: '#333' }}>Flags</h3>
              {booleanSettings.map((s) => (
                <div
                  key={s.setting_key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1vmin',
                    background: 'white',
                    borderRadius: '0.3vmin',
                    border: '1px solid #e0e0e0',
                    marginBottom: '0.5vmin',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1.6vmin', fontWeight: 'bold' }}>{s.description}</div>
                    <div style={{ fontSize: '1.3vmin', color: '#888' }}>{s.setting_key}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={Boolean(edited[s.setting_key] ?? s.setting_value)}
                    onChange={(e) =>
                      setEdited((prev) => ({ ...prev, [s.setting_key]: e.target.checked }))
                    }
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#2196F3',
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Numeric settings */}
          <div
            style={{
              background: '#f9f9f9',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              border: '1px solid #ddd',
            }}
          >
            <h3 style={{ margin: '0 0 0.5vmin 0', fontSize: '2vmin', color: '#333' }}>
              Filter Parameters
            </h3>
            <p
              style={{
                margin: '0 0 1.5vmin 0',
                fontSize: '1.4vmin',
                color: '#666',
                fontStyle: 'italic',
              }}
            >
              Parameters apply to the selected filter algorithm.
            </p>
            {numericSettings.map((s) => (
              <div
                key={s.setting_key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '1vmin',
                  padding: '1vmin',
                  background: 'white',
                  borderRadius: '0.3vmin',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '1.6vmin', fontWeight: 'bold' }}>{s.description}</div>
                  <div style={{ fontSize: '1.3vmin', color: '#888' }}>{s.setting_key}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5vmin' }}>
                  <input
                    type="number"
                    min="1"
                    value={String(edited[s.setting_key] ?? s.setting_value)}
                    onChange={(e) =>
                      setEdited((prev) => ({
                        ...prev,
                        [s.setting_key]: parseInt(e.target.value) || 0,
                      }))
                    }
                    style={{
                      width: '80px',
                      fontSize: '1.6vmin',
                      padding: '0.5vmin',
                      textAlign: 'center',
                      border:
                        edited[s.setting_key] !== s.setting_value
                          ? '2px solid #2196F3'
                          : '1px solid #ccc',
                      borderRadius: '0.3vmin',
                    }}
                  />
                  <span style={{ fontSize: '1.4vmin', color: '#666', minWidth: '40px' }}>
                    {s.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            flexShrink: 0,
            padding: '1.5vmin 2.5vmin',
            borderTop: '1px solid #ddd',
            background: 'white',
          }}
        >
          {hasChanges() && (
            <div
              style={{
                marginBottom: '1vmin',
                fontSize: '1.4vmin',
                color: '#2196F3',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              Unsaved changes
            </div>
          )}
          <div style={{ display: 'flex', gap: '1vmin' }}>
            <Button
              text={saving ? 'Saving...' : 'Save Changes'}
              onClick={handleSave}
              disabled={saving || !hasChanges()}
              style={{
                flex: 1,
                background: hasChanges() ? '#4CAF50' : '#ccc',
                color: 'white',
                fontSize: '1.8vmin',
                padding: '1.2vmin 2vmin',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor: hasChanges() && !saving ? 'pointer' : 'not-allowed',
                opacity: hasChanges() && !saving ? 1 : 0.6,
              }}
            />
            {!inline && (
              <Button
                text="Cancel"
                onClick={onClose}
                disabled={saving}
                style={{
                  flex: 1,
                  background: '#ccc',
                  color: '#333',
                  fontSize: '1.8vmin',
                  padding: '1.2vmin 2vmin',
                  borderRadius: '0.5vmin',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionFilterSettings;
